import React, { useEffect, useState } from 'react';
import {
  Plus,
  Eye,
  PencilSimple,
  Trash,
  Users
} from '@phosphor-icons/react';
import Alerts from '../../../shared/components/ui/Alerts';
import ActionMenu from '../../../shared/components/ui/ActionMenu';
import GenericTables from '../../../shared/components/ui/GenericTables';
import PageHeader from '../../../shared/components/ui/PageHeader';
import ModalCreationEditing from '../../../shared/components/ui/ModalCreationEditing';
import { isDuplicateEmailError, userService } from '../../../services/users/userService.js';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [userModalMode, setUserModalMode] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);
  const [showAlerts, setshowAlerts] = useState(false);
  const [showAlertsContenido, setshowAlertsContenido] = useState('');
  const [showAlertsTitle, setshowAlertsTitle] = useState('Aviso');
  const [deleteUser, setDeleteUser] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success', origin: '' });

  const fetchUsers = async (page = currentPage, size = pageSize) => {
    try {
      setLoading(true);
      const effectivePage = Number(page) || 1;
      const effectivePageSize = Number(size) || 10;
      const response = await userService.getUsers({
        pageSize: effectivePageSize,
        pageCurrent: effectivePage - 1
      });

      const usersResult = Array.isArray(response) ? response : response?.users || [];
      setUsers(usersResult);

      if (!Array.isArray(response)) {
        const totalItems = Number(response?.pagination?.totalItems) || usersResult.length;
        const resolvedPageSize = Number(response?.pagination?.pageSize) || effectivePageSize;
        const calculatedPages = totalItems > 0 ? Math.ceil(totalItems / resolvedPageSize) : 1;

        setTotalUsers(totalItems);
        setTotalPages(calculatedPages);
      } else {
        setTotalUsers(usersResult.length);
        setTotalPages(1);
      }

      setError(null);
    } catch (err) {
      setError('Error al cargar usuarios');
      setUsers([]);
      setTotalUsers(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize]);

  // Función para mostrar notificaciones
  const showNotification = (message, type = 'success', origin = '') => {
    setNotification({ show: true, message, type, origin });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success', origin: '' });
    }, 3000);
  };

  const handleInputChange = (e, origin) => {
    const { name, value } = e.target;
    // Permitimos logs para debugging durante desarrollo
    // Manejo especial para teléfono: sólo dígitos y aplicar al estado correcto según origen
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      if (origin === 'create') {
        setNewUser(prev => ({ ...prev, [name]: digitsOnly }));
      } else if (origin === 'edit') {
        setEditUser(prev => ({ ...prev, [name]: digitsOnly }));
      } else {
        setNewUser(prev => ({ ...prev, [name]: digitsOnly }));
      }
      return;
    }

    if (origin === 'create') {
      setNewUser(prev => ({ ...prev, [name]: value }));
    } else if (origin === 'edit') {
      setEditUser(prev => ({ ...prev, [name]: value }));
    }
  };

  const validarEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // si el correo tiene / es invalido
    if (value.includes('/') || value.includes('\\')) {
      return false;
    }
    return emailRegex.test(value);
  };

  const handleAddUser = async () => {
    if (!newUser.name.trim()) {
      setNewUserTouched(prev => ({ ...prev, name: true }));
      showNotification('El nombre del usuario es requerido', 'error', 'create');
      return;
    }
    if (!newUser.email.trim()) {
      setNewUserTouched(prev => ({ ...prev, email: true }));
      showNotification('El email del usuario es requerido', 'error', 'create');
      return;
    }

    if (!validarEmail(newUser.email)) {
      setNewUserTouched(prev => ({ ...prev, email: true }));
      showNotification('El email del usuario no es válido', 'error', 'create');
      return;
    }
    if (!newUser.password.trim()) {
      setNewUserTouched(prev => ({ ...prev, password: true }));
      showNotification('La contraseña del usuario es requerida', 'error', 'create');
      return;
    }
    if (newUser.password.length < 6) {
      setNewUserTouched(prev => ({ ...prev, password: true }));
      showNotification('La contraseña debe tener al menos 6 caracteres', 'error', 'create');
      return;
    }
    if (!newUser.role) {
      setNewUserTouched(prev => ({ ...prev, role: true }));
      showNotification('El rol del usuario es requerido', 'error', 'create');
      return;
    }
    if (!/^\d{10,15}$/.test(newUser.phone)) {
      setNewUserTouched(prev => ({ ...prev, phone: true }));
      showNotification('El teléfono del usuario no es válido', 'error', 'create');
      return;
    }
    try {
      await userService.createUser(newUser);
      setNewUser({ name: '', email: '', phone: '', role: '', password: '' });
      setShowModal(false);
      await fetchUsers(currentPage, pageSize);
      setError(null);
      return;
    } catch (err) {
      if (isDuplicateEmailError(err)) {
        showNotification('Ya existe un usuario con ese correo electrónico.', 'error', 'create');
        return;
      } else {
        setError('Error al agregar usuario');
      }
    }
  };

  const handleEditUser = async () => {
    // Validar contraseñas si se proporcionan
    if (editUser.password || editUser.confirmPassword) {
      if (!editUser.password) {
        setEditUserTouched(prev => ({ ...prev, password: true }));
        showNotification('Debe ingresar una nueva contraseña', 'error', 'edit');
        return;
      }
      if (!editUser.confirmPassword) {
        setEditUserTouched(prev => ({ ...prev, confirmPassword: true }));
        showNotification('Debe confirmar la nueva contraseña', 'error', 'edit');
        return;
      }
      if (editUser.password !== editUser.confirmPassword) {
        setEditUserTouched(prev => ({ ...prev, password: true, confirmPassword: true }));
        showNotification('Las contraseñas no coinciden', 'error', 'edit');
        return;
      }
      if (editUser.password.length < 6) {
        setEditUserTouched(prev => ({ ...prev, password: true }));
        showNotification('La contraseña debe tener al menos 6 caracteres', 'error', 'edit');
        return;
      }
    }
    if (!editUser.name.trim()) {
      setEditUserTouched(prev => ({ ...prev, name: true }));
      showNotification('El nombre del usuario es requerido', 'error', 'edit');
      return;
    }
    if (!editUser.phone.trim()) {
      setEditUserTouched(prev => ({ ...prev, phone: true }));
      showNotification('El teléfono del usuario es requerido', 'error', 'edit');
      return;
    }
    if (!/^\d{10,13}$/.test(editUser.phone)) {
      setEditUserTouched(prev => ({ ...prev, phone: true }));
      showNotification('El teléfono del usuario no es válido', 'error', 'edit');
      return;
    }
    if (!editUser.role) {
      setEditUserTouched(prev => ({ ...prev, role: true }));
      showNotification('El rol del usuario es requerido', 'error', 'edit');
      return;
    }

    // Borrar atributo password si no se proporcionó una nueva
    if (editUser.password === '') {
      delete editUser.password;
    }

    try {
      await userService.updateUser(editUser.email, editUser);
      setEditUser(null);
      setShowModalEdit(false);
      await fetchUsers(currentPage, pageSize);
      setError(null);
      return;
    } catch (err) {
      setError('Error al actualizar usuario');
      return;
    }
  }

  const handleDeleteUser = async () => {
    try {
      await userService.deleteUser(deleteUser);
      await fetchUsers(currentPage, pageSize);
      return;
    } catch (err) {
      setError('Error al eliminar usuario');
    }
  };

  const handleUserSubmit = async (values) => {
    const isCreating = userModalMode === 'create';
    const payload = {
      name: values.name?.trim() || '',
      email: values.email?.trim() || '',
      phone: values.phone || '',
      role: values.role || ''
    };

    if (values.password) {
      payload.password = values.password;
    }

    try {
      setIsSubmittingUser(true);

      if (isCreating) {
        await userService.createUser(payload);
      } else {
        await userService.updateUser(selectedUser.email, payload);
      }

      await fetchUsers(currentPage, pageSize);
      setError(null);
      closeModal();
    } catch (err) {
      if (isCreating && isDuplicateEmailError(err)) {
        showNotification('Ya existe un usuario con ese correo electrónico.', 'error', 'user-modal');
      } else {
        setError(isCreating ? 'Error al agregar usuario' : 'Error al actualizar usuario');
      }
    } finally {
      setIsSubmittingUser(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-error/12 text-error border-error';
      case 'technician': return 'bg-info/12 text-info border-info';
      case 'operator': return 'bg-warning/12 text-warning border-warning';
      default: return 'bg-surface-2 text-content-muted border-line';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Administrador';
      case 'technician': return 'Técnico';
      case 'operator': return 'Operador';
      default: return role;
    }
  };

  const openModal = () => {
    setSelectedUser(null);
    setUserModalMode('create');
  };

  const closeModal = () => {
    setUserModalMode(null);
    setSelectedUser(null);
  };

  const openModalEdit = (user) => {
    setSelectedUser(user);
    setUserModalMode('edit');
  };

  const openModalView = (user) => {
    setSelectedUser(user);
    setUserModalMode('view');
  };

  const userModalFields = [
    {
      name: 'name',
      label: 'Nombre de usuario',
      type: 'text',
      required: true,
      maxLength: 220,
      placeholder: 'Nombre de usuario',
      disabled: (_, mode) => mode === 'view'
    },
    {
      name: 'email',
      label: 'Correo electrónico',
      type: 'email',
      required: true,
      maxLength: 220,
      placeholder: 'Correo electrónico',
      disabled: (_, mode) => mode !== 'create',
      validate: (value) => validarEmail(value) ? null : 'Ingresa un correo electrónico válido'
    },
    {
      name: 'phone',
      label: 'Teléfono',
      type: 'custom',
      required: true,
      render: ({ value, onChange, onBlur, disabled }) => (
        <input
          type="tel"
          inputMode="numeric"
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value.replace(/\D/g, ''))}
          onBlur={onBlur}
          disabled={disabled}
          placeholder="Teléfono"
          className="w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line disabled:opacity-70"
        />
      ),
      disabled: (_, mode) => mode === 'view',
      validate: (value, _, mode) => {
        const pattern = mode === 'create' ? /^\d{10,15}$/ : /^\d{10,13}$/;
        return pattern.test(value || '') ? null : `El teléfono debe tener entre 10 y ${mode === 'create' ? 15 : 13} dígitos`;
      }
    },
    {
      name: 'role',
      label: 'Rol',
      type: 'select',
      required: true,
      placeholder: 'Selecciona un rol',
      options: [
        { value: 'operator', label: 'Operador' },
        { value: 'technician', label: 'Técnico' },
        { value: 'admin', label: 'Administrador' }
      ],
      disabled: (_, mode) => mode === 'view'
    },
    {
      name: 'password',
      label: userModalMode === 'edit' ? 'Nueva contraseña' : 'Contraseña',
      type: 'password',
      required: (_, mode) => mode === 'create',
      minLength: 6,
      minLengthMessage: 'La contraseña debe tener al menos 6 caracteres',
      placeholder: userModalMode === 'edit' ? 'Nueva contraseña' : 'Contraseña',
      helperText: userModalMode === 'edit' ? 'Deja en blanco para mantener la actual.' : undefined,
      hidden: (_, mode) => mode === 'view',
      validate: (value, values, mode) => (
        mode === 'edit' && (value || values.confirmPassword) && !value
          ? 'Debe ingresar una nueva contraseña'
          : null
      )
    },
    {
      name: 'confirmPassword',
      label: 'Confirmar nueva contraseña',
      type: 'password',
      placeholder: 'Confirmar nueva contraseña',
      hidden: (_, mode) => mode !== 'edit',
      validate: (value, values) => {
        if (values.password || value) {
          if (!value) return 'Debe confirmar la nueva contraseña';
          if (value !== values.password) return 'Las contraseñas no coinciden';
        }
        return null;
      }
    }
  ];


  if (error) {
    return (
      <div className="bg-error/12 border border-error rounded-lg p-4 mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-error">⚠</span>
          <span className="text-error">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Gestión de Usuarios"
        subtitle="Administra los usuarios de tu sistema"
        icon={<Users size={28} />}
        actionButton={{
          label: 'Agregar Usuario',
          icon: <Plus size={18} />,
          onClick: openModal,
        }}
      />
      <div className="space-y-6">

        <ModalCreationEditing
          isOpen={Boolean(userModalMode)}
          mode={userModalMode || 'create'}
          title={
            userModalMode === 'view'
              ? 'Ver usuario'
              : userModalMode === 'edit'
                ? 'Editar usuario'
                : 'Agregar nuevo usuario'
          }
          icon={userModalMode === 'view' ? <Eye size={22} /> : userModalMode === 'edit' ? <PencilSimple size={22} /> : <Plus size={22} />}
          fields={userModalFields}
          initialValues={{
            name: selectedUser?.name || '',
            email: selectedUser?.email || '',
            phone: selectedUser?.phone || '',
            role: selectedUser?.role || '',
            password: '',
            confirmPassword: ''
          }}
          submitText={userModalMode === 'edit' ? 'Guardar cambios' : 'Agregar usuario'}
          isSubmitting={isSubmittingUser}
          notification={notification.origin === 'user-modal' ? notification : null}
          onSubmit={userModalMode === 'view' ? undefined : handleUserSubmit}
          onClose={closeModal}
          maxWidthClassName="max-w-2xl"
          columns={2}
          footer={
            userModalMode === 'view' ? (
              <div className="flex justify-end gap-4 pt-6 border-t border-line mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 bg-surface-2 text-content rounded-lg hover:bg-surface transition-colors"
                >
                  Cerrar
                </button>
              </div>
            ) : undefined
          }
        />

        <div className="relative task-section md:overflow-hidden md:rounded-xl md:border md:border-line md:bg-surface md:shadow-md">
          <GenericTables
            columns={[
              {
                key: 'name',
                label: 'Usuario',
                align: 'center',
                render: (row) => row.name || 'Sin nombre'
              },
              {
                key: 'email',
                label: 'Correo',
                align: 'center',
                render: (row) => row.email || 'Sin correo'
              },
              {
                key: 'role',
                label: 'Rol',
                align: 'center',
                isChip: true,
                render: (row) => (
                  <span className={`text-xs font-medium px-2 py-1 rounded-full border ${getRoleColor(row.role)}`}>
                    {getRoleLabel(row.role)}
                  </span>
                )
              },
              {
                key: 'actions',
                label: 'Acciones',
                align: 'center',
                isAction: true,
                render: (row) => (
                  <ActionMenu
                    options={[
                      { label: 'Ver', icon: <Eye size={16} />, onClick: () => openModalView(row) },
                      { label: 'Editar', icon: <PencilSimple size={16} />, onClick: () => openModalEdit(row) },
                      {
                        label: 'Eliminar',
                        icon: <Trash size={16} />,
                        onClick: () => {
                          setDeleteUser(row.email);
                          setshowAlertsTitle('Confirmar Eliminación');
                          setshowAlertsContenido('¿Estás seguro de que quieres eliminar este usuario?');
                          setshowAlerts(true);
                        }
                      }
                    ]}
                  />
                )
              }
            ]}
            data={users}
            showFooter={true}
            pagination={{
              currentPage,
              totalPages,
              pageSize,
              totalItems: totalUsers,
              pageSizeOptions: [5, 10, 20, 50],
              onPageChange: setCurrentPage,
              onPageSizeChange: (newSize) => {
                setPageSize(newSize);
                setCurrentPage(1);
              }
            }}
            onRowClick={(user) => openModalView(user)}
          />
          {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-bg/60">
                <div className="flex items-center gap-3 rounded-lg border border-line bg-surface/90 px-4 py-3 text-sm text-content shadow-lg">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-amber border-t-transparent" />
                  Consultando usuarios...
                </div>
              </div>
            )}
        </div>

        {showAlerts && (
          <Alerts title={showAlertsTitle} onClose={() => setshowAlerts(false)} width='max-w-md'>
            <h2 className="text-lg font-semibold">{showAlertsContenido}</h2>

            {/* Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-line">
              <button
                type="button"
                onClick={() => {
                  setshowAlerts(false);
                  setDeleteUser(null);
                }}
                className="px-4 py-2 text-content-muted bg-surface-2 rounded-md hover:bg-surface"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-error text-white rounded-md hover:opacity-90"
                onClick={() => { handleDeleteUser(); setshowAlerts(false); }}
              >
                Eliminar
              </button>
            </div>

          </Alerts>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
